import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { getCriancaAtiva, listarAlimentos } from "../../db/queries";
import { salvarFoto } from "../../lib/foto";
import { ESCADA_MAX, PASSO_ESCADA_LABEL, type PassoEscada } from "../../types";
import { criarNotaSessao, listarProfissionais } from "../queries";
import { resultadoGeraOferta } from "../domain";
import { EspecialidadeBadge } from "../ui/EspecialidadeBadge";
import { EscadaDisciplina } from "../ui/EscadaDisciplina";
import type { AnexoClinico, SensibilidadeAnexo, TipoAnexo } from "../types";

/**
 * P0.2 — Nota de Sessão. Caminho rápido: escolher profissional → objetivo + degrau →
 * salvar. Detalhes (atividades/orientações/observações/anexos) ficam colapsados.
 * Se ligar a um alimento + marcar o degrau observado, grava Oferta(origem=profissional).
 */
function tipoDoBlob(t: string): TipoAnexo {
  if (t.startsWith("image")) return "foto";
  if (t.startsWith("audio")) return "audio";
  if (t.startsWith("video")) return "video";
  return "documento";
}

export function RegistrarSessao() {
  const { profissionalId } = useParams();
  const nav = useNavigate();
  const crianca = useLiveQuery(getCriancaAtiva);
  const profissionais = useLiveQuery(() => listarProfissionais()) ?? [];
  const alimentos = useLiveQuery(() => listarAlimentos()) ?? [];

  const [selProf, setSelProf] = useState<string | null>(profissionalId ?? null);
  const [objetivo, setObjetivo] = useState("");
  const [alimentoId, setAlimentoId] = useState("");
  const [degrauAlvo, setDegrauAlvo] = useState<number | "">("");
  const [degrauObs, setDegrauObs] = useState<PassoEscada | null>(null);
  const [maisDetalhes, setMaisDetalhes] = useState(false);
  const [atividades, setAtividades] = useState("");
  const [orientacoes, setOrientacoes] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [anexos, setAnexos] = useState<AnexoClinico[]>([]);
  const [salvo, setSalvo] = useState(false);

  const prof = profissionais.find((p) => p.id === selProf) ?? null;

  async function anexar(file: File | undefined, sensibilidade: SensibilidadeAnexo) {
    if (!file) return;
    const blob_id = await salvarFoto(file);
    setAnexos((a) => [...a, { blob_id, tipo: tipoDoBlob(file.type), sensibilidade }]);
  }

  async function salvar() {
    if (!prof || !crianca || !objetivo.trim()) return;
    await criarNotaSessao({
      crianca_id: crianca.id,
      profissional_id: prof.id,
      especialidade: prof.especialidade,
      objetivo,
      alimento_id: alimentoId || null,
      degrau_alvo: degrauAlvo === "" ? null : (degrauAlvo as PassoEscada),
      degrau_observado: degrauObs,
      atividades: atividades.split(",").map((s) => s.trim()).filter(Boolean),
      orientacoes_casa: orientacoes || null,
      observacoes: observacoes || null,
      anexos,
    });
    setSalvo(true);
    setTimeout(() => nav("/elo", { replace: true }), 900);
  }

  if (salvo) {
    return (
      <main className="tela">
        <div className="vazio">
          <span className="emoji" aria-hidden>🔗</span>
          <div style={{ fontSize: "1.3rem", color: "var(--accent-forte)" }}>sessão registrada!</div>
        </div>
      </main>
    );
  }

  // passo 1 — escolher profissional
  if (!prof) {
    return (
      <main className="tela">
        <button className="btn btn-fantasma" onClick={() => nav(-1)}>← voltar</button>
        <h1 className="tela-titulo">registrar sessão</h1>
        <p className="tela-sub">de qual profissional é a sessão?</p>
        {profissionais.length === 0 ? (
          <div className="vazio">
            <span className="emoji" aria-hidden>🔗</span>
            <p>cadastre um profissional primeiro.</p>
            <Link to="/elo/equipe/novo" className="btn btn-primario" style={{ marginTop: "var(--s3)" }}>
              ➕ cadastrar profissional
            </Link>
          </div>
        ) : (
          <>
            <Link to="/elo/equipe/novo" className="card linha-alimento">
              <div className="foto" aria-hidden>➕</div>
              <div className="crescer">
                <div className="nome">cadastrar novo profissional</div>
              </div>
            </Link>
            {profissionais.map((p) => (
              <button className="card linha-alimento" key={p.id} onClick={() => setSelProf(p.id)}>
                <div className="foto" aria-hidden>👤</div>
                <div className="crescer">
                  <div className="row-entre">
                    <div className="nome">{p.nome}</div>
                    <EspecialidadeBadge especialidade={p.especialidade} curto />
                  </div>
                </div>
              </button>
            ))}
          </>
        )}
      </main>
    );
  }

  // passo 2 — a nota
  const geraOferta = resultadoGeraOferta({ alimento_id: alimentoId || null, degrau_observado: degrauObs });
  return (
    <main className="tela">
      <button className="btn btn-fantasma" onClick={() => (profissionalId ? nav(-1) : setSelProf(null))}>
        ← trocar profissional
      </button>

      <div className="row" style={{ margin: "var(--s2) 0 var(--s4)" }}>
        <div className="foto" aria-hidden style={{ width: 48, height: 48 }}>👤</div>
        <div className="crescer">
          <div className="nome">{prof.nome}</div>
          <EspecialidadeBadge especialidade={prof.especialidade} curto />
        </div>
      </div>

      <div className="card">
        <div className="campo" style={{ margin: 0 }}>
          <label>objetivo da sessão</label>
          <input
            type="text"
            value={objetivo}
            onChange={(e) => setObjetivo(e.target.value)}
            placeholder="ex.: tolerar brócolis no prato"
            autoFocus
          />
        </div>
      </div>

      <section className="secao" style={{ marginTop: "var(--s4)" }}>
        <h2 className="secao-titulo">resultado na escada <span style={{ color: "var(--text-suave)" }}>(opcional)</span></h2>
        <EscadaDisciplina especialidade={prof.especialidade} valor={degrauObs} onChange={setDegrauObs} />
        {alimentos.length > 0 && (
          <div className="card" style={{ marginTop: "var(--s3)" }}>
            <div className="campo">
              <label>ligar a um alimento <span style={{ color: "var(--text-suave)" }}>(objetivo compartilhado)</span></label>
              <select value={alimentoId} onChange={(e) => setAlimentoId(e.target.value)}>
                <option value="">—</option>
                {alimentos.map((a) => (
                  <option key={a.id} value={a.id}>{a.nome}</option>
                ))}
              </select>
            </div>
            {alimentoId && (
              <div className="campo" style={{ margin: 0 }}>
                <label>degrau-alvo</label>
                <select value={String(degrauAlvo)} onChange={(e) => setDegrauAlvo(e.target.value === "" ? "" : Number(e.target.value))}>
                  <option value="">—</option>
                  {Array.from({ length: ESCADA_MAX + 1 }, (_, i) => (
                    <option key={i} value={i}>{i} · {PASSO_ESCADA_LABEL[i as PassoEscada]}</option>
                  ))}
                </select>
              </div>
            )}
            {geraOferta && (
              <p className="status-regra" style={{ marginTop: "var(--s2)" }}>
                ✓ isso vai registrar uma oferta de terapia e mover a escada do alimento.
              </p>
            )}
          </div>
        )}
      </section>

      <section className="secao">
        <h2 className="secao-titulo">orientações para casa <span style={{ color: "var(--text-suave)" }}>(o que o cuidador lê)</span></h2>
        <div className="card">
          <div className="campo" style={{ margin: 0 }}>
            <textarea
              value={orientacoes}
              onChange={(e) => setOrientacoes(e.target.value)}
              placeholder="o que a família aplica em casa até a próxima sessão (ex.: servir brócolis junto, sem cobrança)"
            />
          </div>
        </div>
      </section>

      <button
        className="btn btn-fantasma btn-bloco"
        onClick={() => setMaisDetalhes((v) => !v)}
        style={{ marginBottom: "var(--s3)" }}
      >
        {maisDetalhes ? "▾ menos detalhes" : "▸ mais detalhes (atividades, observações, anexos)"}
      </button>

      {maisDetalhes && (
        <div className="card" style={{ marginBottom: "var(--s4)" }}>
          <div className="campo">
            <label>atividades <span style={{ color: "var(--text-suave)" }}>(separe por vírgula)</span></label>
            <input type="text" value={atividades} onChange={(e) => setAtividades(e.target.value)} placeholder="ex.: dessensibilização, brincadeira com textura" />
          </div>
          <div className="campo">
            <label>observações clínicas</label>
            <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} placeholder="ex.: preciso que a TO trabalhe a textura antes" />
          </div>
          <div className="campo" style={{ margin: 0 }}>
            <label>anexos {anexos.length > 0 && <span style={{ color: "var(--accent-forte)" }}>· {anexos.length}</span>}</label>
            <div className="row" style={{ gap: "var(--s2)" }}>
              <label className="btn btn-fantasma crescer" style={{ cursor: "pointer" }}>
                📎 normal
                <input type="file" accept="image/*,audio/*,video/*" hidden onChange={(e) => { void anexar(e.target.files?.[0], "normal"); e.target.value = ""; }} />
              </label>
              <label className="btn btn-fantasma crescer" style={{ cursor: "pointer" }}>
                🔒 sensível
                <input type="file" accept="image/*,audio/*,video/*" hidden onChange={(e) => { void anexar(e.target.files?.[0], "restrito_anel"); e.target.value = ""; }} />
              </label>
            </div>
            <p className="status-regra" style={{ marginTop: "var(--s2)" }}>
              anexos <strong>sensíveis</strong> (antes/depois) ficam <strong>fora do export</strong>.
            </p>
          </div>
        </div>
      )}

      <button className="btn btn-primario btn-bloco btn-grande" disabled={!objetivo.trim()} onClick={salvar}>
        salvar sessão
      </button>
    </main>
  );
}
